import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthState, LoginRequest, RegisterRequest, ChangePasswordRequest, UpdateProfileRequest } from '../types';
import { authApi, setAuthToken, getAuthToken } from '../utils/api';

interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (userData: UpdateProfileRequest) => Promise<void>;
  changePassword: (passwordData: ChangePasswordRequest) => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  firstLoginSetupComplete: boolean;
  setFirstLoginSetupComplete: (value: boolean) => void;
  requiresFirstLoginSetup: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: getAuthToken(),
    loading: true,
    error: null,
  });

  // First login setup: require both profile and password change
  const [firstLoginSetupComplete, setFirstLoginSetupCompleteState] = useState<boolean>(() => {
    // Initialize from localStorage
    return !!localStorage.getItem('firstLoginSetupComplete');
  });
  
  const setFirstLoginSetupComplete = (value: boolean) => {
    setFirstLoginSetupCompleteState(value);
    if (value) {
      localStorage.setItem('firstLoginSetupComplete', 'true');
    } else {
      localStorage.removeItem('firstLoginSetupComplete');
    }
  };

  // Check default admin status from backend
  const checkDefaultAdminStatus = async () => {
    try {
      const response = await authApi.checkDefaultAdminStatus();
      const isUpdated = response.data.default_admin_updated;
      
      // Only update state if we don't already have it set to true locally
      if (!firstLoginSetupComplete) {
        setFirstLoginSetupCompleteState(isUpdated);
        if (isUpdated) {
          localStorage.setItem('firstLoginSetupComplete', 'true');
        }
      } else {
        // Keep local state as true, not overriding with backend response
      }
      return isUpdated;
    } catch (error) {
      // Fallback to localStorage
      const stored = !!localStorage.getItem('firstLoginSetupComplete');
      if (!firstLoginSetupComplete) {
        setFirstLoginSetupCompleteState(stored);
      }
      return stored;
    }
  };

  // Show setup if not complete and user is admin or superadmin (by role)
  const requiresFirstLoginSetup = (state.user?.role === 'admin' || state.user?.role === 'superadmin') && !firstLoginSetupComplete;
  
  // Debug logging for setup requirements
  console.log('[AuthContext] Setup check - user role:', state.user?.role, 'firstLoginSetupComplete:', firstLoginSetupComplete, 'requiresFirstLoginSetup:', requiresFirstLoginSetup);

  const clearError = () => {
    setState(prev => ({ ...prev, error: null }));
  };

  const checkAuth = async () => {
    const token = getAuthToken();
    if (!token) {
      setState(prev => ({ ...prev, loading: false, isAuthenticated: false, user: null }));
      return;
    }

    try {
      const result = await authApi.checkAuthStatus();
      setState(prev => ({
        ...prev,
        isAuthenticated: result.authenticated,
        user: result.user,
        loading: false,
        error: null,
      }));

      // If user is admin or superadmin, check default admin status
      if (result.authenticated && (result.user?.role === 'admin' || result.user?.role === 'superadmin')) {
        await checkDefaultAdminStatus();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setState(prev => ({
        ...prev,
        isAuthenticated: false,
        user: null,
        loading: false,
        error: error instanceof Error ? error.message : 'Authentication check failed',
      }));
      setAuthToken(null);
    }
  };

  const login = async (credentials: LoginRequest) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await authApi.login(credentials);
      setState(prev => ({
        ...prev,
        isAuthenticated: true,
        user: response.user,
        token: response.token,
        loading: false,
        error: null,
      }));

      // The setup gate is only evaluated in checkAuth(), which runs on mount.
      // Without this, an admin who logs in client-side lands on the setup
      // screen until the page is reloaded.
      if (response.user?.role === 'admin' || response.user?.role === 'superadmin') {
        await checkDefaultAdminStatus();
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Login failed',
      }));
      throw error;
    }
  };

  const register = async (userData: RegisterRequest) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await authApi.register(userData);
      setState(prev => ({
        ...prev,
        isAuthenticated: true,
        user: response.user,
        token: response.token,
        loading: false,
        error: null,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Registration failed',
      }));
      throw error;
    }
  };

  const logout = async () => {
    setState(prev => ({ ...prev, loading: true }));
    
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with logout even if API call fails
    }
    
    // Clear all authentication data
    localStorage.removeItem('authToken');
    localStorage.removeItem('firstLoginSetupComplete');
    localStorage.removeItem('adminPasswordChanged');
    
    setState({
      isAuthenticated: false,
      user: null,
      token: null,
      loading: false,
      error: null,
    });
  };

  const updateProfile = async (userData: UpdateProfileRequest) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const updatedUser = await authApi.updateProfile(userData);
      setState(prev => ({
        ...prev,
        user: updatedUser,
        loading: false,
        error: null,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Profile update failed',
      }));
      throw error;
    }
  };

  const changePassword = async (passwordData: ChangePasswordRequest) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      await authApi.changePassword(passwordData);
      setState(prev => ({ ...prev, loading: false, error: null }));
      // Mark first login setup as complete after password change
      setFirstLoginSetupComplete(true);
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Password change failed',
      }));
      throw error;
    }
  };

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    checkAuth,
    clearError,
    firstLoginSetupComplete,
    setFirstLoginSetupComplete,
    requiresFirstLoginSetup,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 